/**
 * Empréstimos/adiantamentos (E7b) — cadastro e listagem com saldo devedor
 * derivado; a baixa da parcela é automática no cálculo da folha mensal.
 * Padrão Mantine (U5) com confirmação no cancelamento.
 */
import { useEffect, useState } from "react";
import { Alert, Badge, Button, Card, Group, NumberInput, Select, Text, TextInput, ThemeIcon, Title } from "@mantine/core";
import { IconCash } from "@tabler/icons-react";
import { api } from "../api";
import { fmtMoeda, fmtCompetencia } from "../format";
import { CompetenciaField, MoedaField } from "../componentes/entradas-mantine";
import { Confirmacao, notificarOk, notificarErroApi } from "../componentes/notificacoes";
import { Carregando, Vazio } from "../componentes/Estados";
import { ItemEntidade } from "../componentes/listas";

interface Emprestimo {
  id: string;
  descricao: string;
  principalCentavos: number;
  parcelasTotal: number;
  valorParcelaCentavos: number;
  competenciaInicio: number;
  eventoCodigoDominio: number;
  status: string;
  saldoDevedorCentavos: number | null;
  funcionario: { nome: string; matriculaDominio: number | null };
}
interface Funcionario {
  id: string;
  nome: string;
}

const COR_STATUS: Record<string, string> = { ativo: "sillis", quitado: "gray", cancelado: "red" };

export function EmprestimosPage() {
  const [lista, setLista] = useState<Emprestimo[] | null>(null);
  const [funcionarios, setFuncionarios] = useState<Funcionario[]>([]);
  const [erro, setErro] = useState<unknown>(null);
  const [comp, setComp] = useState<number | null>(202607);
  const [fFunc, setFFunc] = useState("");
  const [fDesc, setFDesc] = useState("");
  const [fPrincipal, setFPrincipal] = useState<number | null>(null);
  const [fParcelas, setFParcelas] = useState<number>(1);
  const [fParcela, setFParcela] = useState<number | null>(null);
  const [fInicio, setFInicio] = useState<number | null>(202607);
  const [cancelando, setCancelando] = useState<string | null>(null);
  const [fEvento, setFEvento] = useState("981");

  async function carregar() {
    try {
      const r = await api.get<{ emprestimos: Emprestimo[] }>(`/emprestimos?competencia=${comp}`);
      setLista(r.emprestimos);
    } catch (e) {
      setErro(e);
    }
  }

  useEffect(() => {
    void carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comp]);

  useEffect(() => {
    api
      .get<{ funcionarios: Funcionario[] }>("/funcionarios?tipoVinculo=clt&ativo=true")
      .then((r) => {
        setFuncionarios(r.funcionarios);
        if (r.funcionarios.length > 0) setFFunc(r.funcionarios[0]!.id);
      })
      .catch(setErro);
  }, []);

  async function cadastrar() {
    try {
      setErro(null);
      await api.post("/emprestimos", {
        funcionarioId: fFunc,
        descricao: fDesc,
        principalCentavos: fPrincipal,
        parcelasTotal: fParcelas,
        valorParcelaCentavos: fParcela,
        competenciaInicio: fInicio,
        eventoCodigoDominio: Number(fEvento),
      });
      notificarOk("Empréstimo cadastrado — a baixa entra automaticamente no cálculo da folha.");
      setFDesc("");
      setFPrincipal(null);
      setFParcela(null);
      await carregar();
    } catch (e) {
      setErro(e);
      notificarErroApi(e);
    }
  }

  async function cancelar(id: string) {
    try {
      setErro(null);
      await api.post(`/emprestimos/${id}/cancelar`);
      notificarOk("Empréstimo cancelado — não desconta mais nas próximas folhas.");
      await carregar();
    } catch (e) {
      setErro(e);
      notificarErroApi(e);
    }
  }

  return (
    <>
      <Group justify="space-between" mb={4}>
        <Title order={2}>Empréstimos &amp; adiantamentos</Title>
        <CompetenciaField label="Saldo na competência" valor={comp} onChange={setComp} />
      </Group>
      <Text size="sm" c="dimmed" mb="md">
        Cadastre uma vez — a parcela desconta sozinha na folha de cada mês, com quitação automática na última.
      </Text>
      {erro != null && (
        <Alert color="red" title="Falha ao carregar" mb="sm">
          {String(erro)}
        </Alert>
      )}

      <Card withBorder shadow="xs" mb="md">
        <Title order={5} mb="sm">Novo empréstimo</Title>
        <Group align="flex-end" wrap="wrap">
          <Select label="Pessoa" data={funcionarios.map((f) => ({ value: f.id, label: f.nome }))} value={fFunc} onChange={(v) => setFFunc(v ?? "")} w={260} />
          <TextInput label="Descrição" value={fDesc} onChange={(e) => setFDesc(e.currentTarget.value)} w={220} />
          <MoedaField label="Principal" valor={fPrincipal} onChange={setFPrincipal} w={110} />
          <NumberInput label="Nº parcelas" value={fParcelas} onChange={(v) => setFParcelas(Number(v) || 1)} min={1} max={60} w={90} />
          <MoedaField label="Parcela" valor={fParcela} onChange={setFParcela} w={110} />
          <CompetenciaField label="1ª parcela em" valor={fInicio} onChange={setFInicio} />
          <Select
            label="Evento Domínio"
            data={[
              { value: "981", label: "981 — adiantamento salarial" },
              { value: "9750", label: "9750 — consignado crédito trabalhador" },
            ]}
            value={fEvento}
            onChange={(v) => setFEvento(v ?? "981")}
            w={240}
          />
          <Button onClick={() => void cadastrar()} disabled={!fDesc || fPrincipal === null || fParcela === null || fInicio === null}>
            Cadastrar
          </Button>
        </Group>
        <Text size="xs" c="dimmed" mt="sm">
          A última parcela ajusta o troco se parcela × n ≠ principal. Quitação automática ao vencer a última parcela.
        </Text>
      </Card>

      {!lista ? (
        <Carregando linhas={4} />
      ) : lista.length === 0 ? (
        <Vazio texto={`Nenhum empréstimo com saldo em ${comp ? fmtCompetencia(comp) : "—"}.`} />
      ) : (
        lista.map((e) => (
          <ItemEntidade
            key={e.id}
            icone={
              <ThemeIcon variant="light" size="lg" color={e.status === "ativo" ? "sillis" : "gray"}>
                <IconCash size={18} />
              </ThemeIcon>
            }
            titulo={`${e.funcionario.nome} — ${e.descricao}`}
            linhaSecundaria={`${fmtMoeda(e.valorParcelaCentavos)} × ${e.parcelasTotal} · início ${fmtCompetencia(e.competenciaInicio)} · evento ${e.eventoCodigoDominio} · principal ${fmtMoeda(e.principalCentavos)}`}
            badges={
              <>
                <Badge color={COR_STATUS[e.status] ?? "gray"}>{e.status}</Badge>
                <Badge variant="outline" color={e.saldoDevedorCentavos ? "sillis" : "gray"}>
                  saldo {e.saldoDevedorCentavos === null ? "—" : fmtMoeda(e.saldoDevedorCentavos)}
                </Badge>
              </>
            }
            acoes={
              e.status === "ativo"
                ? [{ rotulo: "Cancelar empréstimo", cor: "red.7", onClick: () => setCancelando(e.id) }]
                : undefined
            }
          />
        ))
      )}

      <Confirmacao
        aberta={cancelando !== null}
        titulo="Cancelar este empréstimo?"
        consequencia="O empréstimo é marcado como cancelado e NÃO gera mais desconto nas folhas seguintes (parcelas já descontadas não voltam). Se foi cadastrado por engano, prefira cancelar a apagar o histórico."
        rotuloConfirmar="Cancelar empréstimo"
        onConfirmar={() => {
          void cancelar(cancelando!);
          setCancelando(null);
        }}
        onCancelar={() => setCancelando(null)}
      />
    </>
  );
}
